import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  Pagination,
  InputAdornment,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format, parseISO, isPast, isFuture, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const AdminBookingsPage = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Current users dialog state
  const [currentUsersDialog, setCurrentUsersDialog] = useState(false);
  const [currentUsers, setCurrentUsers] = useState(null);
  const [loadingCurrentUsers, setLoadingCurrentUsers] = useState(false);

  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);

  // Available options for filters
  const [facilities, setFacilities] = useState([]);
  
  const itemsPerPage = 10;

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      if (response.data.success) {
        setFacilities(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching facilities:', err);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (facilityFilter) params.facility = facilityFilter;
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) {
        const today = new Date();
        // Set to start/end of day in local timezone before converting to ISO
        const startOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);

        switch (dateFilter) {
          case 'today':
            params.startDate = startOfToday.toISOString();
            params.endDate = endOfToday.toISOString();
            break;
          case 'upcoming':
            params.startDate = new Date().toISOString();
            break;
          case 'past':
            params.endDate = new Date().toISOString();
            break;
          default:
            // No date filter applied
            break;
        }
      }

      const response = await api.get('/bookings/admin/all', { params });
      
      if (response.data.success) {
        setBookings(response.data.data || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalBookings(response.data.pagination?.total || 0);
        setError('');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch bookings';
      setError(errorMessage);
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, facilityFilter, statusFilter, dateFilter]);

  useEffect(() => {
    fetchFacilities();
    fetchBookings();
  }, [currentPage, searchUser, facilityFilter, statusFilter, dateFilter, fetchFacilities, fetchBookings]);

  const handleCancelBooking = (booking) => {
    setSelectedBooking(booking);
    setDeleteDialog(true);
  };

  const confirmCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      setDeleteLoading(true);
      
      await api.put(`/bookings/admin/${selectedBooking._id}/cancel`);
      
      setSnackbar({
        open: true,
        message: 'Booking cancelled successfully',
        severity: 'success'
      });
      
      setDeleteDialog(false);
      setSelectedBooking(null);
      
      // Refresh bookings
      fetchBookings();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to cancel booking';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error deleting booking:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: newStatus });
      
      setSnackbar({
        open: true,
        message: 'Booking status updated successfully',
        severity: 'success'
      });
      
      fetchBookings();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update booking status';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error updating booking status:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'completed':
        return <CheckCircleIcon color="primary" />;
      case 'no-show':
        return <ErrorIcon color="warning" />;
      default:
        return <CheckCircleIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'primary';
      case 'no-show':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getBookingTimeStatus = (startTime, endTime) => {
    const start = parseISO(startTime);
    const end = parseISO(endTime);

    if (isPast(end)) return 'past';
    if (isToday(start)) return 'today';
    if (isFuture(start)) return 'upcoming';
    return 'current';
  };

  const formatDateTime = (dateTimeString) => {
    const date = parseISO(dateTimeString);
    return {
      date: format(date, 'MMM d, yyyy'),
      time: format(date, 'h:mm a'),
      full: format(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a')
    };
  };

  const handleDialogClose = () => {
    setDeleteDialog(false);
    setSelectedBooking(null);
  };

  const clearFilters = () => {
    setSearchUser('');
    setFacilityFilter('');
    setStatusFilter('');
    setDateFilter('');
    setCurrentPage(1);
  };

  const fetchCurrentUsers = async () => {
    try {
      setLoadingCurrentUsers(true);
      const response = await api.get('/bookings/admin/current-users');
      
      if (response.data.success) {
        setCurrentUsers(response.data.data);
        setCurrentUsersDialog(true);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch current users';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error fetching current users:', err);
    } finally {
      setLoadingCurrentUsers(false);
    }
  };

  if (loading && currentPage === 1) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <AdminIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          {t('admin.bookings.title', { ns: 'admin' })}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('admin.bookings.subtitle', { ns: 'admin' })}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{xs:12 , md:3}}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('admin.bookings.filters.searchPlaceholder', { ns: 'admin' })}
              value={searchUser}
              onChange={(e) => {
                setSearchUser(e.target.value);
                setCurrentPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{xs:12 , md:2}}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('admin.bookings.filters.facility', { ns: 'admin' })}
              value={facilityFilter}
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="">{t('admin.bookings.filters.allFacilities', { ns: 'admin' })}</MenuItem>
              {facilities.map((facility) => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{xs:12 , md:2}}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('admin.bookings.filters.status', { ns: 'admin' })}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="">{t('admin.bookings.filters.allStatuses', { ns: 'admin' })}</MenuItem>
              <MenuItem value="confirmed">{t('common:status.confirmed')}</MenuItem>
              <MenuItem value="cancelled">{t('common:status.cancelled')}</MenuItem>
              <MenuItem value="completed">{t('common:status.completed')}</MenuItem>
              <MenuItem value="no-show">{t('admin.bookings.filters.noShow', { ns: 'admin' })}</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{xs:12 , md:2}}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('admin.bookings.filters.dateRange', { ns: 'admin' })}
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="">{t('admin.bookings.filters.allDates', { ns: 'admin' })}</MenuItem>
              <MenuItem value="today">{t('admin.bookings.filters.today', { ns: 'admin' })}</MenuItem>
              <MenuItem value="upcoming">{t('admin.bookings.filters.upcoming', { ns: 'admin' })}</MenuItem>
              <MenuItem value="past">{t('admin.bookings.filters.past', { ns: 'admin' })}</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{xs:12 , md:3}}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                startIcon={<FilterIcon />}
              >
                {t('admin.bookings.filters.clearFilters', { ns: 'admin' })}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchBookings}
                startIcon={<RefreshIcon />}
              >
                {t('admin.bookings.filters.refresh', { ns: 'admin' })}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Summary */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {totalBookings > 0
            ? t('admin.bookings.summary.showing', {
                ns: 'admin',
                start: ((currentPage - 1) * itemsPerPage) + 1,
                end: Math.min(currentPage * itemsPerPage, totalBookings),
                total: totalBookings
              })
            : t('admin.bookings.summary.noBookings', { ns: 'admin' })
          }
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={fetchCurrentUsers}
            startIcon={<VisibilityIcon />}
            disabled={loadingCurrentUsers}
            color="secondary"
            sx={{
              minWidth: 140,
              boxShadow: 1,
              '&:hover': { boxShadow: 3 }
            }}
          >
            {loadingCurrentUsers
              ? t('admin.bookings.summary.loading', { ns: 'admin' })
              : t('admin.bookings.summary.currentUsers', { ns: 'admin' })
            }
          </Button>
          
          <Chip 
            icon={<AdminIcon />} 
            label={t('admin.bookings.adminView.label', { ns: 'admin' })} 
            color="primary" 
            variant="outlined" 
          />
        </Box>
      </Box>

      {/* Bookings List */}
      <Grid container spacing={2}>
        {bookings.map((booking) => {
          const startDateTime = formatDateTime(booking.startTime);
          const endDateTime = formatDateTime(booking.endTime);
          const timeStatus = getBookingTimeStatus(booking.startTime, booking.endTime);

          return (
            <Grid size={{xs:12}} key={booking._id}>
              <Card
                sx={{
                  '&:hover': {
                    boxShadow: 4
                  }
                }}
              >
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {/* Status and Basic Info */}
                    <Grid size={{xs:12 , md:3}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getStatusIcon(booking.status)}
                        <Chip
                          label={booking.status}
                          color={getStatusColor(booking.status)}
                          size="small"
                          sx={{ ml: 1, textTransform: 'capitalize' }}
                        />
                      </Box>
                      <Typography variant="h6" component="h3">
                        {booking.facility?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {booking._id.slice(-8).toUpperCase()}
                      </Typography>
                    </Grid>

                    {/* User Information */}
                    <Grid size={{xs:12 , md:3}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2">
                          {booking.user?.firstName} {booking.user?.lastName}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {booking.user?.email}
                      </Typography>
                      {booking.createdBy && booking.createdBy._id !== booking.user._id && (
                        <Typography variant="caption" color="text.secondary">
                          Booked by: {booking.createdBy.firstName} {booking.createdBy.lastName}
                        </Typography>
                      )}
                    </Grid>

                    {/* Date and Time */}
                    <Grid size={{xs:12 , md:3}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2">
                          {startDateTime.date}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {startDateTime.time} - {endDateTime.time}
                      </Typography>
                      <Chip
                        label={
                          timeStatus === 'today' ? 'Today' :
                          timeStatus === 'upcoming' ? 'Upcoming' :
                          timeStatus === 'past' ? 'Past' : 'Current'
                        }
                        size="small"
                        color={
                          timeStatus === 'today' ? 'warning' :
                          timeStatus === 'upcoming' ? 'info' :
                          timeStatus === 'past' ? 'default' : 'success'
                        }
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    {/* Actions */}
                    <Grid size={{xs:12 , md:3}}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {booking.status === 'confirmed' && (
                          <Tooltip title={t('admin:bookings.actions.markCompleted')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleStatusUpdate(booking._id, 'completed')}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {booking.status === 'confirmed' && (
                          <Tooltip title={t('admin:bookings.actions.markNoShow')}>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleStatusUpdate(booking._id, 'no-show')}
                            >
                              <WarningIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title={t('admin:bookings.actions.cancelBooking')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelBooking(booking)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>

                  {booking.notes && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Notes:</strong> {booking.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>
      )}

      {/* Current Users Dialog */}
      <Dialog
        open={currentUsersDialog}
        onClose={() => setCurrentUsersDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
              Current Facility Users
            </Box>
            <IconButton onClick={() => setCurrentUsersDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {currentUsers ? (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{currentUsers.totalActiveUsers} users</strong> are currently using{' '}
                  <strong>{currentUsers.totalFacilitiesInUse} facilities</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {format(new Date(currentUsers.timestamp), 'PPpp')}
                </Typography>
              </Alert>

              {currentUsers.facilities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No users currently using any facilities
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All facilities are currently available
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {currentUsers.facilities.map((facilityData, index) => (
                    <Grid size={{ xs: 12, md: 6 }} key={facilityData.facility._id || index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6">
                              {facilityData.facility.name}
                            </Typography>
                            <Chip
                              label={`${facilityData.userCount} user${facilityData.userCount !== 1 ? 's' : ''}`}
                              color="primary"
                              size="small"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Type: {facilityData.facility.type} | 
                            Capacity: {facilityData.facility.currentOccupancy || 0}/{facilityData.facility.capacity}
                          </Typography>

                          <List dense>
                            {facilityData.users.map((userData, userIndex) => {
                              const startTime = format(new Date(userData.booking.startTime), 'h:mm a');
                              const endTime = format(new Date(userData.booking.endTime), 'h:mm a');
                              
                              return (
                                <ListItem key={userData.booking.id || userIndex} sx={{ px: 0 }}>
                                  <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      <PersonIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                                      <Typography variant="subtitle2">
                                        {userData.user.firstName} {userData.user.lastName}
                                      </Typography>
                                      {userData.user.userType && (
                                        <Chip
                                          label={userData.user.userType}
                                          size="small"
                                          variant="outlined"
                                          sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {userData.user.email}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                      <ScheduleIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {startTime} - {endTime}
                                      </Typography>
                                    </Box>
                                    {userData.booking.notes && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        Note: {userData.booking.notes}
                                      </Typography>
                                    )}
                                  </Box>
                                </ListItem>
                              );
                            })}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setCurrentUsersDialog(false)}>
            Close
          </Button>
          <Button
            onClick={fetchCurrentUsers}
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={loadingCurrentUsers}
          >
            {loadingCurrentUsers ? 'Refreshing...' : 'Refresh'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AdminIcon sx={{ mr: 1, color: 'error.main' }} />
              {t('admin.bookings.dialog.title', { ns: 'admin' })}
            </Box>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedBooking && (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                {t('admin.bookings.dialog.adminOverrideWarning', { ns: 'admin' })}
              </Alert>

              <Alert severity="info" sx={{ mb: 2 }}>
                {t('admin.bookings.dialog.quotaRefundInfo', { ns: 'admin' })}
              </Alert>

              <Grid container spacing={2}>
                <Grid size={{xs:12 , md:6}}>
                  <Typography variant="h6" gutterBottom>
                    {t('admin.bookings.dialog.bookingDetails', { ns: 'admin' })}
                  </Typography>
                  <List>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.facility')}
                        secondary={selectedBooking.facility?.name}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.dateTime')}
                        secondary={`${formatDateTime(selectedBooking.startTime).full} - ${formatDateTime(selectedBooking.endTime).time}`}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.status')}
                        secondary={selectedBooking.status}
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid size={{xs:12 , md:6}}>
                  <Typography variant="h6" gutterBottom>
                    {t('admin.bookings.dialog.userInformation', { ns: 'admin' })}
                  </Typography>
                  <List>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.name')}
                        secondary={`${selectedBooking.user?.firstName} ${selectedBooking.user?.lastName}`}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.email')}
                        secondary={selectedBooking.user?.email}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('admin:bookings.details.bookingId')}
                        secondary={selectedBooking._id}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {selectedBooking.notes && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>{t('admin.bookings.dialog.notes', { ns: 'admin' })}:</strong> {selectedBooking.notes}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleDialogClose}>
            {t('admin.bookings.dialog.cancel', { ns: 'admin' })}
          </Button>
          <Button
            onClick={confirmCancelBooking}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : t('admin.bookings.actions.deletePermanently', { ns: 'admin' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminBookingsPage;